import { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import useEmblaCarousel from "embla-carousel-react";
import { Navbar, Footer, StudyCard, Button, MarqueeRow } from "../components";
import { landingheader4, bgcta, reachusillustration, logo } from "../assets";
import { landingHeaderImgs } from "../constants";
import {
  LandingMarqueeRowLoadingSkeleton,
  LandingProgramStudyLoadingSkeleton,
  LandingUniveralNumbersLoadingSkeleton,
} from "../components/LoadingSkeleton";
import customAxios from "../utils/customAxios";
import ErrorPage from "./ErrorPage";

import { styles } from "../styles";

const LandingPage = () => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const { data, isLoading, isError } = useQuery({
    // The queryKey is a unique array used to cache and deduplicate requests
    queryKey: ["landingPageData"],

    // The queryFn is the asynchronous function that fetches the data
    queryFn: async () => {
      const [programsRes, settingsRes, partnersRes] = await Promise.all([
        customAxios.get("program-studies"),
        customAxios.get("dashboard-settings"),
        customAxios.get("partners"),
      ]);

      const allPartners = partnersRes.data.data || [];

      // Return the formatted object. This will be available as the 'data' variable.
      return {
        programs: programsRes.data.data || [],
        settings: settingsRes.data.data?.[0] || null,
        partners: {
          institutions: allPartners.filter((p) => p.type === "institution"),
          university: allPartners.filter((p) => p.type === "university"),
        },
      };
    },
    // Optional configuration
    staleTime: 1000 * 60 * 5, // Caches the data and prevents refetching for 5 minutes
    refetchOnWindowFocus: false, // Prevents automatic refetching when the user switches browser tabs
  });

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
    containScroll: false,
    skipSnaps: false,
  });

  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (!emblaApi || isPaused) return;

    const interval = setInterval(() => {
      emblaApi.scrollNext();
    }, 3500);

    return () => clearInterval(interval);
  }, [emblaApi, isPaused]);

  const toggleAutoplay = useCallback(() => {
    setIsPaused((prev) => !prev);
  }, []);

  if (isError) {
    return (
      <ErrorPage
        code="500"
        titleKey="error.500_title"
        defaultTitle="Gagal Memuat Data"
        subtitleKey="error.500_subtitle"
        defaultSubtitle="Terjadi masalah saat terhubung ke server kami. Silakan coba lagi nanti atau kembali ke"
      />
    );
  }

  return (
    <>
      <Navbar />
      <section className="relative grid w-full h-auto place-items-center">
        {/* -- SLIDESHOW --  */}
        <div
          id="slideshowWrapper"
          className="relative w-full h-auto overflow-hidden aspect-video"
        >
          {/* Logo / Scroll indicator */}
          {(isLoading || data?.settings?.show_large_logo) && (
            <img
              src={logo}
              alt="logo"
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-[400px] md:w-[300px] sm:w-[200px]"
            />
          )}

          {/* Embla viewport */}
          <div ref={emblaRef} className="w-full h-full overflow-hidden">
            {/* Embla container */}
            <div className="flex h-full touch-pan-y">
              {isLoading
                ? landingHeaderImgs.map(({ img, position }, index) => (
                    <div
                      key={index}
                      className="relative min-w-0 flex-[0_0_100%] h-auto aspect-video"
                    >
                      <div
                        className="absolute inset-0"
                        style={{
                          background: `linear-gradient(#00000050, #00000050, url(${img}) ${position}/cover no-repeat`,
                        }}
                      />
                    </div>
                  ))
                : data?.settings?.slideshows?.map((link, index) => (
                    <div
                      key={index}
                      className="relative min-w-0 flex-[0_0_100%] h-auto aspect-video"
                    >
                      <div
                        className="absolute inset-0"
                        style={{
                          background: `url(${link}) center/cover no-repeat`,
                        }}
                      />
                    </div>
                  ))}
            </div>
          </div>

          {/* Pause / Resume */}
          <button
            type="button"
            onClick={toggleAutoplay}
            aria-label={isPaused ? "Resume slideshow" : "Pause slideshow"}
            className="absolute z-20 px-4 py-2 text-white transition rounded-full bottom-6 right-6 bg-black/40 backdrop-blur hover:bg-black/60"
          >
            {isPaused ? "▶" : "Ⅱ"}
          </button>
        </div>
        {/* -- END OF SLIDESHOW --  */}

        {/* ---- UNIVERAL STATISTICS ---- */}
        <div className="w-full px-30 grid place-items-center absolute smdlg:static smdlg:bottom-0 bottom-[-108px] mx-30">
          <div className="w-full max-w-[1200px] h-full py-8 smd:rounded-none rounded-[16px] bg-white flex flex-col items-center justify-center gap-6">
            <h2 className={styles.headingM}>
              <Trans i18nKey="landing.statistics.title">
                Univeral dalam Angka
              </Trans>
            </h2>
            <div className="grid w-full grid-cols-4 md:grid-cols-2 sm:grid-cols-1 gap-y-6">
              {isLoading ? (
                <LandingUniveralNumbersLoadingSkeleton />
              ) : (
                <>
                  {/* Lecturers */}
                  <div
                    className={`flex flex-col justify-center items-center gap-2 min-w-[250px] sm:py-1 text-primary-40`}
                  >
                    <h1 className={styles.headingL}>
                      {!!data?.settings ? data?.settings?.total_lecturers : 0}
                    </h1>
                    <p className={styles.textLMedium}>
                      <Trans i18nKey="landing.statistics.subtitle.lecturers" />
                    </p>
                  </div>
                  {/* Students */}
                  <div
                    className={`flex flex-col justify-center items-center gap-2 min-w-[250px] sm:py-1 text-info-40 border-x md:border-r-0 sm:border-x-0 border-black`}
                  >
                    <h1 className={styles.headingL}>
                      {!!data?.settings ? data?.settings?.total_students : 0}
                    </h1>
                    <p className={styles.textLMedium}>
                      <Trans i18nKey="landing.statistics.subtitle.students" />
                    </p>
                  </div>
                  {/* Study Program */}
                  <div
                    className={`flex flex-col justify-center items-center gap-2 min-w-[250px] sm:py-1 text-success-40`}
                  >
                    <h1 className={styles.headingL}>
                      {data?.programs?.length}
                    </h1>
                    <p className={styles.textLMedium}>
                      <Trans i18nKey="landing.statistics.subtitle.studyPrograms" />
                    </p>
                  </div>
                  {/* Alumni */}
                  <div
                    className={`flex flex-col justify-center items-center gap-2 min-w-[250px] sm:py-1 text-amber-600 border-x md:border-r-0 sm:border-x-0 border-black`}
                  >
                    <h1 className={styles.headingL}>
                      {!!data?.settings ? data?.settings?.total_alumni : 0}
                    </h1>
                    <p className={styles.textLMedium}>
                      <Trans i18nKey="landing.statistics.subtitle.alumni" />
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ---- STUDY PROGRAMS ---- */}
      <section
        className={`${styles.paddingX} flex flex-col items-center justify-center w-full gap-20 pt-40 smd:pt-20 pb-20 relative overflow-hidden`}
      >
        <div className="absolute top-0 grid place-items-center h-[100%] w-inherit z-[-1] ">
          <div className="h-[100%] w-full bg-gradient-to-tr from-primary-40 to-primary-80">
            <img
              src={landingheader4}
              alt="unvieral building"
              className="object-cover h-full opacity-40 grayscale smd:opacity-0"
            />
          </div>
        </div>
        <div className="flex flex-col gap-10 ">
          <div className="text-center text-white max-w-225">
            <h1 className={`${styles.headingM} mb-2`}>
              <Trans i18nKey="landing.studyProgram.title" />
            </h1>
            <p>
              <Trans i18nKey="landing.studyProgram.subtitle1" />
              {data?.programs?.length}
              <Trans i18nKey="landing.studyProgram.subtitle2" />
            </p>
          </div>
          <div className="max-w-[1150px] flex flex-wrap items-center justify-center gap-20 ">
            {isLoading ? (
              <LandingProgramStudyLoadingSkeleton />
            ) : (
              data?.programs?.map(
                ({ id, thumbnail, name_en, name_id }, index) => (
                  <StudyCard
                    id={id}
                    key={id}
                    img={thumbnail}
                    subject={currentLanguage === "en" ? name_en : name_id}
                    number={index > 8 ? `${index + 1}` : `0${index + 1}`}
                  />
                ),
              )
            )}
          </div>
        </div>
      </section>

      {/* ---- PARTNERS ---- */}
      <section className={`${styles.paddingX} py-20`}>
        <div className="mb-8 text-center max-w-900">
          <h1 className={`${styles.headingM} mb-3 text-primary-40`}>
            <Trans i18nKey="landing.partners.title" />
          </h1>
          <p>
            <Trans i18nKey="landing.partners.subtitle" />
          </p>
        </div>
        <div className="flex flex-col items-center justify-center gap-4 smd:w-full">
          {isLoading ? (
            // Show Skeletons while data is fetching
            <>
              <LandingMarqueeRowLoadingSkeleton />
              <LandingMarqueeRowLoadingSkeleton />
            </>
          ) : (
            // Show actual Marquee once data is loaded
            <>
              {/* Row 1: Institutions */}
              <MarqueeRow items={data?.partners?.institutions} />

              {/* Row 2: Universities */}
              <MarqueeRow items={data?.partners?.university} />
            </>
          )}
        </div>
      </section>

      <section className="my-20">
        <div
          className={`${styles.paddingX} items-center justify-center w-full py-10`}
        >
          <div className="relative flex items-center w-full gap-8 py-10 rounded-lg h-fit bg-primary-40">
            <div className="absolute flex items-center w-full h-full overflow-hidden">
              <div
                style={{
                  background: `url(${bgcta}) left/cover no-repeat`,
                }}
                className="w-full h-full"
              ></div>
            </div>
            <div className="relative flex items-start justify-between w-full mx-24 smd:mx-12">
              <img
                src={reachusillustration}
                alt="character"
                className="absolute bottom-0 right-0 smd:hidden"
              />
              <div className="flex flex-col justify-center gap-4">
                <h1 className={`${styles.headingLM} text-white`}>
                  <Trans i18nKey="landing.findout.title">
                    Cari tahu lebih dalam <br /> mengenai UNIVERAL
                  </Trans>
                </h1>
                <Link to="/">
                  <Button
                    title={t("landing.findout.btn")}
                    customization="bg-white text-primary-40 hover:bg-neutral-20"
                  />
                </Link>
              </div>
              <div className="w-[266px] smd:hidden"></div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default LandingPage;
